import FileUploadInput from "Common/Components/FileUploadInput/FileUploadInput";
import FormBreadcrumb from "Common/Components/FormBreadcrumb/FormBreadcrumb";
import Colors, { MecordColors } from "Constants/Color";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { fetchProductSalesOrderById } from "slices/sales_order/thunk";
import { fetchCreatePackingPreview } from "slices/Warehouse/Packing/IssuePacking/thunk";
import { createWarehousePacking } from "slices/Warehouse/Packing/PackingDone/thunk";
import { createSelector } from "reselect";
import { format, addDays, parse } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { WarehouseAxios } from "api/WarehouseAxios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { showError, showSuccess, showWarning } from "helpers/swalHelper";
import { Autocomplete, Box } from "@mui/material";

import { autocompleteDropdownStyles } from "Constants/Style";
import LoadingSpinner from "components/LoadingSpinner";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import AddTags from "../components/AddTagsForPacking";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import TextEditor from "components/TextEditor/TextEditor";

interface PackingFormValues {
  packing_date: string;
  initiated_by: string;
  so_number: string;
  notes: string;
  documents: File | null;
  tags: string[];
}

interface SerialNumberOption {
  inventory_product_id: string;
  serial_no: string;
}

const CreatePackingNew: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const [items, setItems] = useState<any>([]);
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [itemErrors, setItemErrors] = useState<Record<number, string>>({});
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [currentSerialNo, setCurrentSerialNo] = useState<string>("");
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [serialNumberOptions, setSerialNumberOptions] = useState<
    SerialNumberOption[]
  >([]);
  const [loadingSerialNumbers, setLoadingSerialNumbers] =
    useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  // Temporary selection inside autocomplete
  const [selectedSerialOptions, setSelectedSerialOptions] = useState<
    SerialNumberOption[]
  >([]);

  // Saved serials per item (already exists)
  const [serialNumbers, setSerialNumbers] = useState<
    Record<number, SerialNumberOption[]>
  >({});

  // Additional Details State
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
  const [additionalDetailsDraft, setAdditionalDetailsDraft] = useState("");
  const [additionalDetailsIndex, setAdditionalDetailsIndex] = useState<number | null>(null);

  // Get sales order data from Redux store
  const selectData = createSelector(
    (state: any) => state.IssuePackingReducer,
    (IssuePackingReducer) => ({
      currentSalesOrder: IssuePackingReducer?.data,
      rowCount: IssuePackingReducer.rowCount,
      salesOrderLoading: IssuePackingReducer.loading,
      salesOrderError: IssuePackingReducer.error,
    }),
  );

  // const selectIssuePackingData = createSelector(
  //   (state: any) => state.IssuePackingReducer,
  //   (IssuePackingReducer) => ({
  //     issuePacking: IssuePackingReducer?.issuePacking,
  //     issuePackingRowCount: IssuePackingReducer.rowCount,
  //     issuePackingLoading: IssuePackingReducer.loading,
  //     issuePackingError: IssuePackingReducer.error,
  //   })
  // );

  // Get packing state from Redux store
  const selectPackingData = createSelector(
    (state: any) => state.WarehousePackingDone,
    (packing) => ({
      createLoading: packing?.createLoading || false,
      createSuccess: packing?.createSuccess || false,
      packingError: packing?.error || null,
    }),
  );

  const { currentSalesOrder, rowCount, salesOrderLoading, salesOrderError } =
    useSelector(selectData);
  const { createLoading, createSuccess, packingError } =
    useSelector(selectPackingData);
  const {
    issuePacking,
    issuePackingRowCount,
    issuePackingLoading,
    issuePackingError,
  } = useSelector((state: any) => state.IssuePackingReducer);

  useEffect(() => {
    console.log("Sales Order Redux:", issuePacking);
  }, [issuePacking]);

  // Set default packing date (22-Aug-25 format)
  const getDefaultDate = () => format(new Date(), "yyyy-MM-dd");

  // Parse date string to Date object for DatePicker
  const getDateFromString = (dateString: string): Date | null => {
    if (!dateString) return null;
    try {
      // Try parsing with dd-MMM-yy format first
      let parsed = parse(dateString, "dd-MMM-yy", new Date());
      if (isNaN(parsed.getTime())) {
        // Try dd-MMM-yyyy format
        parsed = parse(dateString, "dd-MMM-yyyy", new Date());
      }
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  };

  const fetchSerialNumbers = async (
    productId: string,
    warehouseId?: string,
  ) => {
    if (!productId) {
      setSerialNumberOptions([]);
      return;
    }

    setLoadingSerialNumbers(true);
    try {
      const endpoint =
        API_ENDPOINT.WAREHOUSE.ITEM_INWARD.GRN.SERIAL_NO_DROPDOWN(
          productId,
          warehouseId,
        );
      const response = await WarehouseAxios.get(endpoint);

      // Handle different response structures
      const serialNumbersData = response?.data?.data || response?.data || [];
      const serialNumbersList: SerialNumberOption[] = Array.isArray(
        serialNumbersData,
      )
        ? serialNumbersData.map((item: any) => {
            // Handle different response formats
            if (typeof item === "string")
              return { serial_no: item, inventory_product_id: "" };
            return {
              serial_no:
                item?.serial_no || item?.serial_number || item?.value || "",
              inventory_product_id: item?.inventory_product_id || "",
            };
          })
        : [];

      setSerialNumberOptions(serialNumbersList);
    } catch (error: any) {
      console.error("Error fetching serial numbers:", error);
      showError("Failed to fetch serial numbers");
      setSerialNumberOptions([]);
    } finally {
      setLoadingSerialNumbers(false);
    }
  };

  const handleOpenModal = async (item: any, idx: number) => {
    setSelectedSerialOptions([]);
    setSelectedItem(item);
    setSelectedItemIndex(idx);
    // Always load serial numbers from the item (in case user closed without saving previously)
    const existingSerials = item?.serial_numbers || [];
    setSerialNumbers((prev) => ({ ...prev, [idx]: existingSerials }));
    setCurrentSerialNo("");

    // Fetch serial numbers from API
    const productId = item?.product_id || item?.product || item?.id;
    // Get warehouse_id from Redux (same as listing page) or from item/sales order
    let selectedWarehouseId: string | undefined;

    // Check if warehouse_id is in the item (override if available)
    if (item?.warehouse_id) {
      selectedWarehouseId = item.warehouse_id;
    }
    // Check if warehouse_id is in sales order (override if available)
    else if (issuePacking?.warehouse_id) {
      selectedWarehouseId = issuePacking.warehouse_id;
    }

    await fetchSerialNumbers(productId, selectedWarehouseId);
    setShowModal(true);
  };

  // const handleAddSerialNumber = () => {
  //   if (!currentSerialNo) {
  //     showWarning({ text: "Please select a serial number" });
  //     return;
  //   }

  //   if (selectedItemIndex === -1 || !selectedItem) return;

  //   const orderQty = selectedItem?.ordered_quantity || 0;
  //   const currentSerials = serialNumbers[selectedItemIndex] || [];

  //   if (currentSerials.length >= orderQty) {
  //     toast.error(
  //       `Maximum ${orderQty} serial numbers can be added for this item`
  //     );
  //     return;
  //   }

  //   // Check for duplicate serial numbers
  //   if (currentSerials.some((s) => s.serial_no === currentSerialNo)) {
  //     showWarning({ text: "This serial number is already added" });
  //     return;
  //   }

  //   const selectedOption = serialNumberOptions.find(
  //     (opt) => opt.serial_no === currentSerialNo
  //   );

  //   if (selectedOption) {
  //     // Add serial number (don't update items array yet - only on Save)
  //     setSerialNumbers((prev) => ({
  //       ...prev,
  //       [selectedItemIndex]: [...currentSerials, selectedOption],
  //     }));
  //   }

  //   // Clear selection
  //   setCurrentSerialNo("");
  // };

  const handleAddSerialNumber = () => {
    if (!selectedSerialOptions.length) {
      showWarning({ text: "Please select serial numbers" });
      return;
    }

    const existingSerials = serialNumbers[selectedItemIndex] || [];
    const balanceQty =
      (selectedItem?.balance_quantity || 0) - existingSerials.length;

    if (selectedSerialOptions.length > balanceQty) {
      toast.error(`Cannot select more than ${balanceQty} serial numbers`);
      return;
    }

    setSerialNumbers((prev) => ({
      ...prev,
      [selectedItemIndex]: [...existingSerials, ...selectedSerialOptions],
    }));

    // Clear selection after add
    setSelectedSerialOptions([]);
  };

  // const handleRemoveSerialNumber = (serialNo: string) => {
  //   if (selectedItemIndex === -1) return;

  //   const currentSerials = serialNumbers[selectedItemIndex] || [];
  //   const updatedSerials = currentSerials.filter(
  //     (s) => s.serial_no !== serialNo
  //   );

  //   // Remove serial number (don't update items array yet - only on Save)
  //   setSerialNumbers((prev) => ({
  //     ...prev,
  //     [selectedItemIndex]: updatedSerials,
  //   }));
  // };

  const handleRemoveSerialNumber = (serialNo: string) => {
    const currentSerials = serialNumbers[selectedItemIndex] || [];

    setSerialNumbers((prev) => ({
      ...prev,
      [selectedItemIndex]: currentSerials.filter(
        (s) => s.serial_no !== serialNo,
      ),
    }));
  };

  const handleSaveModal = () => {
    const totalSerials = Object.values(serialNumbers).flat();

    if (totalSerials.length === 0) {
      showWarning({ text: "Please add serial numbers before saving" });
      return;
    }

    if (selectedItemIndex === -1) return;

    const currentSerials = serialNumbers[selectedItemIndex] || [];
    const orderQty = selectedItem?.ordered_quantity || 0;

    // Validation: Cannot exceed order quantity
    if (currentSerials.length > orderQty) {
      showWarning({ text: `Cannot add more than ${orderQty} serial numbers` });
      return;
    }

    // Update the item with serial numbers
    const updatedItems = [...items];
    updatedItems[selectedItemIndex].serial_numbers = currentSerials;
    updatedItems[selectedItemIndex].packed_quantity = currentSerials.length;
    setItems(updatedItems);

    // Clear any previous errors for this item
    setItemErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[selectedItemIndex];
      return newErrors;
    });

    showSuccess("Serial numbers saved successfully");
    setShowModal(false);
    setCurrentSerialNo("");
    setSelectedSerialOptions([]);
  };

  const formik = useFormik<PackingFormValues>({
    initialValues: {
      packing_date: getDefaultDate(),
      initiated_by: "",
      so_number: "",
      notes: "",
      documents: null,
      tags: [],
    },
    validationSchema: Yup.object({
      packing_date: Yup.string().required("Packing Date is required"),
      initiated_by: Yup.string()
        .required("Initiated By is required")
        .min(2, "Initiated By must be at least 2 characters"),
      so_number: Yup.string().required("SO Number is required"),
      notes: Yup.string(),
      documents: Yup.mixed().nullable(),
    }),
    onSubmit: async (values) => {
      // Validation 1: At least one item is required
      if (!items || items.length === 0) {
        showWarning({ text: "At least one item is required for packing" });
        return;
      }

      // Validation 2: At least one item must have serial numbers (packed)
      const itemsWithSerialNumbers = items.filter(
        (item: any) =>
          item?.serial_numbers &&
          Array.isArray(item.serial_numbers) &&
          item.serial_numbers.length > 0,
      );

      if (itemsWithSerialNumbers.length === 0) {
        showWarning({
          text: "At least one item must have serial numbers added (packed quantity must be greater than 0)",
        });
        return;
      }

      // Validation 3: Check if any item has serial numbers exceeding ordered quantity
      const hasExceededQuantity = items.some((item: any, idx: number) => {
        const serialNums = item?.serial_numbers || [];
        const orderedQty = item?.ordered_quantity || 0;
        return serialNums.length > orderedQty;
      });

      if (hasExceededQuantity) {
        showWarning({
          text: "Packed quantity (serial numbers) cannot exceed ordered quantity for any item",
        });
        return;
      }

      // Validation 4: Check for any validation errors in itemErrors state
      if (Object.keys(itemErrors).length > 0) {
        showWarning({ text: "Please fix validation errors before submitting" });
        return;
      }

      // Create FormData for payload
      const formData = new FormData();

      // Add basic fields
      formData.append(
        "packing_date",
        new Date(values.packing_date).toISOString(),
      );
      formData.append("initiated_by", values.initiated_by);
      formData.append("sales_order_product_id", id || "");
      formData.append("notes", values.notes || "");
      if (values.tags && values.tags.length > 0) {
        formData.append("tags", JSON.stringify(values.tags));
      }
      const currentWarehouseId =
        issuePacking?.warehouse_id || items?.[0]?.warehouse_id || "";
      formData.append("warehouse_id", currentWarehouseId);

      const fullAddress = issuePacking?.shipping_addresses?.find(
        (addr: any) => addr.shipping_address_id === selectedAddress,
      );
      if (selectedAddress) {
        formData.append("shipping_address_id", selectedAddress);
        if (fullAddress) {
          formData.append("shipping_address", JSON.stringify(fullAddress));
        }
      }

      // Add document if exists
      if (values.documents) {
        formData.append("attachments", values.documents);
      }

      const packingProducts: any[] = [];

      itemsWithSerialNumbers.forEach((item: any) => {
        const serials = item.serial_numbers || [];

        serials.forEach((serialItem: SerialNumberOption) => {
          packingProducts.push({
            sales_order_product_item_id:
              item?.sales_order_product_item_id || item?.item_id || item?.id,

            product_id: item?.product_id,
            inventory_product_id: serialItem.inventory_product_id,

            serial_number: serialItem.serial_no,

            product_snapshot: {
              item_name: item?.item_name,
              product_code: item?.product_code,
              category: item?.category,
              subcategory: item?.subcategory,
              make: item?.make,
              model: item?.model,
              unit_name: item?.unit_name,
              product_sku: item?.product_sku,
              hsn_code: item?.product_hsn,
              shipping_address: fullAddress,
            },
            additional_details: item?.additional_details || "",
          });
        });
      });

      formData.append("packing_products", JSON.stringify(packingProducts));

      // Log the payload for debugging
      console.log("Payload being sent:");
      console.log("packing_date:", values.packing_date);
      console.log("initiated_by:", values.initiated_by);
      console.log("sales_order_id:", id);
      console.log("notes:", values.notes);

      console.log("File check - value:", values.documents);
      if (values.documents) {
        console.log("File check - isFile:", values.documents instanceof File);
        console.log("File check - isBlob:", values.documents instanceof Blob);
        console.log("File check - type:", typeof values.documents);
        console.log("File check - name:", values.documents.name);
        console.log("File check - size:", values.documents.size);
      } else {
        console.log("File check: No document selected");
      }

      console.log("attachments:", values.documents);
      console.log("product_details:", packingProducts);

      // Dispatch the create packing thunk with FormData

      try {
        await dispatch(createWarehousePacking(formData)).unwrap();
        showSuccess("Packing Created successfully").then(() => {
          navigate("/warehouse-packing");
        });
      } catch (error) {
        console.error("Error Created packing:", error);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount);

  useEffect(() => {
    if (id) {
      dispatch(fetchCreatePackingPreview(id));
    }
  }, [dispatch, id]);

  useEffect(() => {}, [issuePacking]);

  // Update items when sales order data is loaded
  useEffect(() => {
    if (issuePacking?.items) {
      const updatedArray = issuePacking.items.map((item: any, idx: number) => {
        const serialNums = item?.serial_numbers || [];
        return {
          ...item,
          item_name: item?.product_name || "N/A",
          packed_quantity: serialNums.length > 0 ? serialNums.length : 0,
          previously_packed_quantity: item?.packed_quantity || 0,
          sales_order_id: item?.sales_order_id || "N/A",
          unit: item?.unit || "N/A",
          product_code: item?.product_code || "N/A",
          category: item?.category_name || "N/A",
          category_name: item?.category_name || "N/A",
          subcategory: item?.sub_category_name || "N/A",
          subcategory_name: item?.sub_category_name || "N/A",
          sub_category: item?.sub_category_name || "N/A",
          make: item?.make_name || "N/A",
          make_name: item?.make_name || "N/A",
          model: item?.model_name || "N/A",
          model_name: item?.model_name || "N/A",
          item_id: item?.item || item?.id,
          item: item?.item || item?.id,
          ordered_quantity: item?.ordered_quantity || 0,
          quantity: item?.packed_quantity || 0,
          balance_quantity: item?.balance_quantity || 0,
          packing_status: item?.packing_status || "",
          in_stock: item?.in_stock || 0,
          serial_no: item?.serial_no || "",
          serial_numbers: serialNums,
          batch_no: item?.batch_no || "",
          pallet_no: item?.pallet_no || "",
          item_code: item?.item_code || "",
          sku: item?.sku || "",
          hsn_code: item?.hsn_code || "",
          qc: item?.qc || "",
          shipping_address: item?.shipping_address || "",
          shipping_id: item?.shipping_id || "",
          shipping_address_id: item?.shipping_address_id || "",
          remarks: item?.remarks || "",
          additional_details: item?.additional_details || "",
        };
      });

      // Initialize serial numbers state
      const serialsState: Record<number, SerialNumberOption[]> = {};
      updatedArray.forEach((item: any, idx: number) => {
        if (item?.serial_numbers && item.serial_numbers.length > 0) {
          serialsState[idx] = item.serial_numbers;
        }
      });
      if (Object.keys(serialsState).length > 0) {
        setSerialNumbers(serialsState);
      }

      formik.setFieldValue("so_number", issuePacking?.sales_order_number || "");
      if (issuePacking?.sales_order_number)
        setTimeout(() => formik.setFieldError("so_number", undefined), 0);
      // Autofill tags from Sales Order when preview loads; user can add more via AddTags
      const soTags = issuePacking?.tags ?? [];
      if (Array.isArray(soTags) && soTags.length > 0) {
        formik.setFieldValue("tags", soTags);
      }
      setData(issuePacking);
      setItems(updatedArray);
    }
  }, [issuePacking, id]);

  // Handle sales order loading and error states
  useEffect(() => {
    if (salesOrderError) {
      showError("Failed to fetch sales order data");
    }
  }, [salesOrderError]);

  // Handle create packing success and error states
  useEffect(() => {
    if (createSuccess) {
      showSuccess("Packing created successfully").then(() => {
        navigate("/warehouse-packing");
      });
    }
  }, [createSuccess, navigate]);

  useEffect(() => {
    if (packingError) {
      const errorMessage =
        typeof packingError === "string"
          ? packingError
          : packingError?.message || "Failed to create packing";
      showError(errorMessage);
    }
  }, [packingError]);

  const handleChange = (idx: number, field: string, value: string) => {
    const updatedItems = [...items];
    let val = field === "remarks" ? value : value === "" ? "" : Number(value);

    // Validate packed_quantity against ordered_quantity
    if (field === "packed_quantity") {
      const numValue = value === "" ? 0 : Number(value);
      const orderedQty = updatedItems[idx]?.ordered_quantity || 0;

      // Clear previous error for this item
      setItemErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[idx];
        return newErrors;
      });

      // Check if packed quantity exceeds ordered quantity
      if (numValue > orderedQty) {
        setItemErrors((prev) => ({
          ...prev,
          [idx]: `Packed quantity cannot exceed ordered quantity (${orderedQty})`,
        }));
      }
    }

    updatedItems[idx][field] = val as never;
    setItems(updatedItems);
  };

  return (
    <>
      <div
        className="page-content px-4"
        style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
      >
        <Container
          fluid
          style={{
            backgroundColor: "#fff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 0 15px rgba(0,0,0,0.1)",
          }}
        >
          <FormBreadcrumb
            formName="Create Packing"
            isEdit={false}
            navigatePath="/warehouse-packing"
            isPrefixRequired={false}
          />
          <hr />

          {pageLoading ||
          createLoading ||
          salesOrderLoading ||
          issuePackingLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "60vh" }}
            >
              <LoadingSpinner />
            </div>
          ) : (
            <Form autoComplete="off" onSubmit={formik.handleSubmit}>
              {/* Packing Details Section */}
              <div className="mb-4">
                <h5 className="fw-bold mb-3">Packing Details</h5>
                <Row
                  style={{ backgroundColor: MecordColors.LightGrayColor }}
                  className="p-2  border-radius-xl"
                >
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Packing Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="packing_date"
                        value={formik.values.packing_date}
                        onChange={(e) => {
                          formik.handleChange(e);
                          if ((e.target.value || "").trim())
                            setTimeout(
                              () =>
                                formik.setFieldError("packing_date", undefined),
                              0,
                            );
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!(
                            formik.errors.packing_date &&
                            formik.touched.packing_date
                          )
                        }
                      />
                      {formik.errors.packing_date &&
                        formik.touched.packing_date && (
                          <Form.Control.Feedback type="invalid">
                            {formik.errors.packing_date}
                          </Form.Control.Feedback>
                        )}
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>
                        Initiated By <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="initiated_by"
                        placeholder="Enter Name"
                        value={formik.values.initiated_by}
                        onChange={(e) => {
                          formik.handleChange(e);
                          const v = (e.target.value || "").trim();
                          if (v.length >= 2)
                            setTimeout(
                              () =>
                                formik.setFieldError("initiated_by", undefined),
                              0,
                            );
                        }}
                        onBlur={formik.handleBlur}
                        isInvalid={
                          !!formik.errors.initiated_by &&
                          formik.touched.initiated_by
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.initiated_by}
                      </Form.Control.Feedback>
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>SO Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="so_number"
                        value={formik.values.so_number}
                        disabled
                      />
                    </div>
                  </Col>
                  {/* <Col lg={3}>
                      <div className="mb-3">
                        <Form.Label>Shipping Address</Form.Label>
                        <Form.Select
                          value={selectedAddress}
                          onChange={(e) => setSelectedAddress(e.target.value)}
                        >
                          <option value="">Select Shipping Address</option>
                          {issuePacking?.shipping_addresses?.map((addr: any) => (
                            <option key={addr.shipping_address_id} value={addr.shipping_address_id}>
                              {`${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pin_code || addr.pincode}`}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </Col> */}
                  <Col lg={3}>
                    <div className="mb-3">
                      <Form.Label>Shipping Address</Form.Label>

                      <Autocomplete
                        options={issuePacking?.shipping_addresses || []}
                        getOptionLabel={(addr: any) =>
                          addr
                            ? `${addr.address}, ${addr.city}, ${
                                addr.state
                              }, ${addr.country} - ${
                                addr.pin_code || addr.pincode
                              }`
                            : ""
                        }
                        value={
                          issuePacking?.shipping_addresses?.find(
                            (a: any) =>
                              a.shipping_address_id === selectedAddress,
                          ) || null
                        }
                        onChange={(_, value) => {
                          setSelectedAddress(value?.shipping_address_id || "");
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.shipping_address_id ===
                          value.shipping_address_id
                        }
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderInput={(params) => (
                          <BootstrapTextField
                            {...params}
                            placeholder="Select Shipping Address"
                            size="small"
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                            }}
                          />
                        )}
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Tags: autofilled from Sales Order when preview loads; user can add more */}
              <Row>
                <Col lg={4} className="mb-4">
                  <AddTags
                    label="Add Tags"
                    placeholder="Enter tags"
                    value={formik.values.tags ?? []}
                    onChange={(tags) => formik.setFieldValue("tags", tags)}
                  />
                </Col>
              </Row>

              {/* Product Details Section */}
              <div className="mb-4">
                <h5 className="fw-bold mb-3">Product Details</h5>
                {selectedAddress ? (
                  <div style={{ overflowX: "auto" }}>
                    <table className="table">
                      <thead
                        style={{ backgroundColor: MecordColors.LightGrayColor }}
                      >
                        <tr>
                          <th style={{ minWidth: "80px" }}>S.No</th>
                          <th style={{ minWidth: "200px" }}>Products</th>
                          <th style={{ minWidth: "120px" }}>Product Code</th>
                          <th style={{ minWidth: "120px" }}>Category</th>
                          <th style={{ minWidth: "120px" }}>Sub-Category</th>
                          <th style={{ minWidth: "150px" }}>Make </th>
                          <th style={{ minWidth: "150px" }}>Model </th>
                          <th style={{ minWidth: "120px" }}>Order Quantity</th>
                          <th style={{ minWidth: "150px" }}>Packed Quantity</th>
                          <th style={{ minWidth: "150px" }}>
                            Balance Quantity
                          </th>
                          <th style={{ minWidth: "150px" }}>
                            Additional Details
                          </th>
                          <th
                            style={{
                              minWidth: "100px",
                              position: "sticky",
                              right: 0,
                              background: MecordColors.LightGrayColor,
                              zIndex: 2,
                            }}
                          >
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="align-middle">
                        {items
                          ?.map((item: any, originalIdx: number) => ({
                            ...item,
                            originalIdx,
                          }))
                          ?.filter(
                            (item: any) =>
                              item.shipping_address_id === selectedAddress,
                          )
                          ?.map((item: any) => {
                            const idx = item.originalIdx;
                            const currentSerials = serialNumbers[idx] || [];
                            const currentSessionPackedQty =
                              currentSerials.length;
                            const prevPackedQty =
                              item?.previously_packed_quantity || 0;
                            const totalPackedQty =
                              prevPackedQty + currentSessionPackedQty;
                            const initialBalance = item?.balance_quantity || 0;
                            const currentBalance =
                              initialBalance - currentSessionPackedQty;
                            const orderedQty = item?.ordered_quantity || 0;

                            return (
                              <tr key={item.id || idx}>
                                <td>{idx + 1}</td>
                                <td>
                                  <div>
                                    <div className="fw-semibold">
                                      {item?.item_name || "-"}
                                    </div>
                                    {item?.product_sku && (
                                      <div
                                        style={{
                                          fontSize: "0.75rem",
                                          color: "#6c757d",
                                        }}
                                      >
                                        SKU: {item.product_sku}
                                      </div>
                                    )}
                                    {item?.product_hsn && (
                                      <div
                                        style={{
                                          fontSize: "0.75rem",
                                          color: "#6c757d",
                                        }}
                                      >
                                        HSN: {item.product_hsn}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>{item?.product_code || "-"}</td>
                                <td>{item?.category || item?.category_name || "-"}</td>
                                <td>{item?.subcategory || item?.subcategory_name || "-"}</td>
                                <td>{item?.make || "-"}</td>
                                <td>{item?.model || "-"}</td>
                                <td>{orderedQty}</td>
                                <td>
                                  <div>
                                    {totalPackedQty}
                                    {totalPackedQty > orderedQty && (
                                      <div
                                        style={{
                                          fontSize: "0.75rem",
                                          color: "#dc3545",
                                          marginTop: "2px",
                                        }}
                                      >
                                        (Exceeds order qty!)
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    {currentBalance >= 0 ? currentBalance : 0}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-center">
                                    <EditIcon
                                      style={{
                                        cursor: "pointer",
                                        color: MecordColors.ThemeBGColor,
                                        fontSize: "20px",
                                      }}
                                      onClick={() => {
                                        setAdditionalDetailsIndex(idx);
                                        setAdditionalDetailsDraft(
                                          item?.additional_details ?? "",
                                        );
                                        setShowAdditionalDetailsModal(true);
                                      }}
                                    />
                                  </div>
                                </td>
                                <td
                                  style={{
                                    minWidth: "100px",
                                    position: "sticky",
                                    right: 0,
                                    background: "white",
                                    zIndex: 2,
                                  }}
                                >
                                  <Button
                                    className="btn btn-sm"
                                    disabled={orderedQty === totalPackedQty}
                                    style={{
                                      backgroundColor:
                                        MecordColors.PrimaryButtonBGColor,
                                      color:
                                        MecordColors.PrimaryButtonTextColor,
                                      border: "none",
                                    }}
                                    onClick={() => handleOpenModal(item, idx)}
                                  >
                                    <i className="bi bi-plus-lg me-1"></i>
                                    Add
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    className="text-center py-4 text-muted border rounded"
                    style={{ backgroundColor: "#f8f9fa" }}
                  >
                    Please select a shipping address to see the products.
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div className="mb-4">
                <div
                  className="d-flex justify-content-between align-items-center p-2 rounded"
                  style={{
                    cursor: "pointer",
                    backgroundColor: "#f8f9fa",
                  }}
                  onClick={() => setIsNotesOpen(!isNotesOpen)}
                >
                  <h6
                    className="fw-semibold mb-0"
                    style={{ fontSize: "0.9rem" }}
                  >
                    {isNotesOpen ? ">" : ">"} Notes
                  </h6>
                </div>
                {isNotesOpen && (
                  <div className="p-2">
                    <Form.Group>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="notes"
                        placeholder="Enter notes"
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </Form.Group>
                  </div>
                )}
              </div>

              {/* Attach Files Section */}
              <div className="mb-4">
                <h6 className="fw-semibold mb-2">Attach Files</h6>
                <FileUploadInput label="" name="documents" formik={formik} />
              </div>

              {/* Action Buttons */}
              <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
                <CancelButton onClick={() => navigate(-1)} />
                <PrimarySubmitButton />
              </div>
            </Form>
          )}
        </Container>
      </div>

      <Modal
        show={showModal}
        onHide={() => {
          // Revert serial numbers to what's in the item (discard unsaved changes)
          if (selectedItemIndex !== -1 && selectedItem) {
            const existingSerials = selectedItem?.serial_numbers || [];
            setSerialNumbers((prev) => ({
              ...prev,
              [selectedItemIndex]: existingSerials,
            }));
          }
          setShowModal(false);
          setCurrentSerialNo("");
        }}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Serial Numbers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <>
              {/* Product Details Table */}
              <div className="mb-4">
                <table className="table table-bordered">
                  <thead
                    style={{ backgroundColor: MecordColors.LightGrayColor }}
                  >
                    <tr>
                      <th>Products</th>
                      <th>Product Code</th>
                      <th>Make & Model</th>
                      <th>In stock</th>
                      <th>Order Quantity</th>
                      <th>Packed Quantity</th>
                      <th>Balance Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <div className="fw-semibold">
                            {selectedItem?.product_name || "-"}
                          </div>
                          {selectedItem?.product_sku !== "" && (
                            <div
                              style={{ fontSize: "0.75rem", color: "#6c757d" }}
                            >
                              SKU: {selectedItem.product_sku}
                            </div>
                          )}
                          {selectedItem?.product_hsn && (
                            <div
                              style={{ fontSize: "0.75rem", color: "#6c757d" }}
                            >
                              HSN Code: {selectedItem.product_hsn}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{selectedItem?.product_code || "-"}</td>
                      <td>
                        {selectedItem?.make && selectedItem?.model
                          ? `${selectedItem.make} ${selectedItem.model}`
                          : selectedItem?.make || selectedItem?.model || "-"}
                      </td>
                      {/* <td>{selectedItem?.in_stock || 0}</td> */}
                      <td>
                        {" "}
                        {(() => {
                          const prevInStock = serialNumberOptions.length || 0;
                          const currentSerials =
                            serialNumbers[selectedItemIndex] || [];
                          return prevInStock - currentSerials.length;
                        })()}
                      </td>
                      <td>{selectedItem?.ordered_quantity || 0}</td>
                      <td>
                        <div>
                          {(() => {
                            const prevPacked =
                              selectedItem?.previously_packed_quantity || 0;
                            const currentSerials =
                              serialNumbers[selectedItemIndex] || [];
                            return prevPacked + currentSerials.length;
                          })()}
                        </div>
                      </td>
                      <td>
                        <div>
                          {(() => {
                            const initialBalance =
                              selectedItem?.balance_quantity || 0;
                            const currentSerials =
                              serialNumbers[selectedItemIndex] || [];
                            const currentBalance =
                              initialBalance - currentSerials.length;
                            return currentBalance >= 0 ? currentBalance : 0;
                          })()}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Serial Number Input Section */}
              <div className="mb-4">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Serial No.</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        {loadingSerialNumbers ? (
                          <div className="d-flex align-items-center">
                            <Spinner size="sm" className="me-2" />
                            <span>Loading serial numbers...</span>
                          </div>
                        ) : (
                          // <Form.Select
                          //   value={currentSerialNo}
                          //   onChange={(e) => setCurrentSerialNo(e.target.value)}
                          //   disabled={
                          //     (serialNumbers[selectedItemIndex] || []).length >=
                          //       (selectedItem?.ordered_quantity || 0) ||
                          //     serialNumberOptions.length === 0
                          //   }
                          // >
                          //   <option value="">Select Serial No.</option>
                          //   {serialNumberOptions
                          //     .filter((serialNo) => {
                          //       // Filter out already selected serial numbers
                          //       const currentSerials =
                          //         serialNumbers[selectedItemIndex] || [];
                          //       return !currentSerials.some(
                          //         (s) => s.serial_no === serialNo.serial_no
                          //       );
                          //     })
                          //     .map((serialNo, idx) => (
                          //       <option key={idx} value={serialNo.serial_no}>
                          //         {serialNo.serial_no}
                          //       </option>
                          //     ))}
                          // </Form.Select>
                          <Autocomplete
                            multiple
                            size="small"
                            disableCloseOnSelect
                            options={serialNumberOptions}
                            value={selectedSerialOptions}
                            loading={loadingSerialNumbers}
                            getOptionLabel={(option) => option.serial_no}
                            isOptionEqualToValue={(option, value) =>
                              option.serial_no === value.serial_no
                            }
                            // 🔹 Hide already-added serials
                            filterOptions={(options) => {
                              const existingSerials =
                                serialNumbers[selectedItemIndex] || [];
                              return options.filter(
                                (opt) =>
                                  !existingSerials.some(
                                    (s) => s.serial_no === opt.serial_no,
                                  ),
                              );
                            }}
                            // 🔹 Balance quantity validation
                            onChange={(_, value) => {
                              const balanceQty =
                                (selectedItem?.balance_quantity || 0) -
                                (serialNumbers[selectedItemIndex]?.length || 0);

                              if (value.length > balanceQty) {
                                showWarning({
                                  text: `Cannot select more than ${selectedItem?.balance_quantity ? `${selectedItem.balance_quantity} serial numbers` : "Balance Quantity"}`,
                                });
                                return;
                              }
                              setSelectedSerialOptions(value);
                            }}
                            // ✅ Mecord styled dropdown option
                            renderOption={(props, option, { selected }) => (
                              <li {...props} style={{ padding: 0 }}>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.8,
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "13px",

                                    // Hover
                                    "&:hover": {
                                      backgroundColor: MecordColors.Hover,
                                      color: MecordColors.ActiveText,
                                    },

                                    // Selected
                                    ...(selected && {
                                      backgroundColor:
                                        MecordColors.ActiveBackground,
                                      color: MecordColors.ActiveText,
                                    }),
                                  }}
                                >
                                  <span>{option.serial_no}</span>

                                  {/* Remove from selection */}
                                  {selected && (
                                    <span
                                      style={{
                                        cursor: "pointer",
                                        marginLeft: "10px",
                                        color: MecordColors.ThemeBGColor,
                                        fontWeight: "bold",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSerialOptions((prev) =>
                                          prev.filter(
                                            (s) =>
                                              s.serial_no !== option.serial_no,
                                          ),
                                        );
                                      }}
                                    >
                                      ✕
                                    </span>
                                  )}
                                </Box>
                              </li>
                            )}
                            ListboxProps={
                              autocompleteDropdownStyles.ListboxProps
                            }
                            renderInput={(params) => (
                              <BootstrapTextField
                                {...params}
                                placeholder="Select Serial Numbers"
                                size="small"
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                }}
                              />
                            )}
                            // ✅ Mecord styled tags
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                  index,
                                });
                                return (
                                  <Box
                                    key={option.serial_no}
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      backgroundColor:
                                        MecordColors.ActiveBackground,
                                      borderRadius: "3px",
                                      padding: "2px 6px",
                                      margin: "1px",
                                      fontFamily: "Poppins, sans-serif",
                                      fontSize: "0.75rem",
                                    }}
                                    {...tagProps}
                                  >
                                    {option.serial_no}
                                  </Box>
                                );
                              })
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "#ced4da",
                                },
                                "&:hover fieldset": {
                                  borderColor: "#ced4da",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#ced4da",
                                },
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          onClick={handleAddSerialNumber}
                          disabled={
                            loadingSerialNumbers ||
                            selectedSerialOptions.length === 0
                          }
                          style={{
                            backgroundColor: MecordColors.PrimaryButtonBGColor, // or MecordColors.primary
                            color: MecordColors.PrimaryButtonTextColor,
                            border: MecordColors.PrimaryButtonBGColor,
                          }}
                        >
                          Add
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                {(serialNumbers[selectedItemIndex] || []).length >=
                  (selectedItem?.ordered_quantity || 0) && (
                  <div
                    className="text-warning"
                    style={{ fontSize: "0.875rem" }}
                  >
                    Maximum {selectedItem?.ordered_quantity || 0} serial numbers
                    can be added
                  </div>
                )}
                {serialNumberOptions.length === 0 && !loadingSerialNumbers && (
                  <div
                    className="text-warning"
                    style={{ fontSize: "0.875rem" }}
                  >
                    No serial numbers available for this product
                  </div>
                )}
              </div>

              {/* Added Serial Numbers List */}
              {(serialNumbers[selectedItemIndex] || []).length > 0 && (
                <div className="mb-3">
                  <h6 className="fw-semibold mb-2">Added Serial Numbers:</h6>
                  <div
                    className="border rounded p-2"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    <div className="d-flex flex-wrap gap-2">
                      {(serialNumbers[selectedItemIndex] || []).map(
                        (serialItem, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center gap-2 border rounded px-2 py-1"
                            style={{ backgroundColor: "#f8f9fa" }}
                          >
                            <span>{serialItem.serial_no}</span>
                            <button
                              type="button"
                              className="btn-close btn-close-sm"
                              onClick={() =>
                                handleRemoveSerialNumber(serialItem.serial_no)
                              }
                              aria-label="Remove"
                            ></button>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <CancelButton
            onClick={() => {
              // Revert serial numbers to what's in the item (discard unsaved changes)
              if (selectedItemIndex !== -1 && selectedItem) {
                const existingSerials = selectedItem?.serial_numbers || [];
                setSerialNumbers((prev) => ({
                  ...prev,
                  [selectedItemIndex]: existingSerials,
                }));
              }
              setShowModal(false);
              setCurrentSerialNo("");
            }}
            showConfirmation={false}
          />
          <PrimarySubmitButton onClick={handleSaveModal} />
        </Modal.Footer>
      </Modal>

      {/* Additional Details Modal */}
      <Modal
        show={showAdditionalDetailsModal}
        onHide={() => {
          setShowAdditionalDetailsModal(false);
          setAdditionalDetailsIndex(null);
          setAdditionalDetailsDraft("");
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Additional Details
            {additionalDetailsIndex !== null && items && items[additionalDetailsIndex]
              ? ` - ${items[additionalDetailsIndex]?.item_name || ""}`
              : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: "250px" }}>
          <Form.Group className="mb-0">
            <TextEditor
              value={additionalDetailsDraft}
              onChange={setAdditionalDetailsDraft}
              placeholder="Enter additional details..."
              minHeightPx={180}
              maxHeightPx={300}
              blockImages={true}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <CancelButton
            onClick={() => {
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
            showConfirmation={false}
          />
          <PrimarySubmitButton
            onClick={() => {
              if (additionalDetailsIndex !== null) {
                const updatedItems = [...items];
                updatedItems[additionalDetailsIndex].additional_details =
                  additionalDetailsDraft;
                setItems(updatedItems);
              }
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreatePackingNew;
